import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Buildings, Trophy, Users, Medal, Sword, WhatsappLogo, ShareNetwork, LinkSimple, MapPin, Calendar } from "@phosphor-icons/react";
import { useClubTheme } from "@/context/ThemeContext";

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS_SHORT = ["LUN", "MAR", "MIE", "JUE", "VIE", "SAB", "DOM"];

function setMeta(prop, content, attr = "property") {
  let el = document.querySelector(`meta[${attr}="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, prop); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function PublicClub() {
  const { clubId } = useParams();
  useClubTheme(clubId);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/club/${clubId}`).then((r) => r.ok ? r.json() : Promise.reject(r)).then(setData).catch(() => setErr(true));
    setMeta("og:type", "website");
    setMeta("og:url", window.location.href);
  }, [clubId]);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.name} · Disfruta y Diviertete`;
    setMeta("og:title", `${data.name} · DyD Padel`);
    setMeta("og:description", `${data.courts_count} canchas · ${data.matches_count} partidos jugados · ${data.top_players.length} jugadores activos`);
  }, [data]);

  if (err) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-gray-400">Club no encontrado</div>;
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  const shareUrl = window.location.href;
  const shareText = `${data.name} · ${data.matches_count} partidos · DyD Padel`;
  const copy = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Enlace copiado"); } catch { toast.error("No se pudo copiar"); } };
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank");
  const shareNative = async () => {
    if (navigator.share) { try { await navigator.share({ title: data.name, text: shareText, url: shareUrl }); } catch { /* user dismissed */ } }
    else copy();
  };

  const opDays = Array.isArray(data.operating_days) ? data.operating_days.map((d) => DAYS_SHORT[d]).join(" · ") : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6 sm:p-10" data-testid="public-club-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <Link to="/" className="inline-block text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] hover:text-white transition-colors">DISFRUTA Y DIVIERTETE · PADEL</Link>

        <div className="relative overflow-hidden bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-white/10 p-8 sm:p-10" data-testid="public-club-hero">
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#CCFF00]" />
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-2 flex items-center gap-2"><Buildings size={14} />Club afiliado</p>
              <h1 className="font-['Barlow_Condensed'] text-5xl sm:text-6xl font-black uppercase tracking-tighter">{data.name}</h1>
              <div className="flex gap-2 mt-3 flex-wrap text-sm text-gray-400">
                {data.city && <span className="flex items-center gap-1"><MapPin size={14} />{data.city}{data.state ? `, ${data.state}` : ""}</span>}
                {opDays && <span className="flex items-center gap-1"><Calendar size={14} />{opDays}</span>}
              </div>
              {data.description && <p className="text-sm text-gray-400 mt-3 max-w-prose">{data.description}</p>}
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div><p className="font-['Barlow_Condensed'] text-4xl font-black text-[#CCFF00]">{data.courts_count}</p><p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Canchas</p></div>
              <div><p className="font-['Barlow_Condensed'] text-4xl font-black text-[#CCFF00]">{data.matches_count}</p><p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Partidos</p></div>
              <div><p className="font-['Barlow_Condensed'] text-4xl font-black text-[#CCFF00]">{data.top_players.length}</p><p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Jugadores</p></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="public-club-ranking">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Medal size={14} />Ranking</p>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4">Top Jugadores del Club</h2>
            {data.top_players.length === 0 ? <p className="text-gray-500 text-sm">Sin jugadores aun</p> : (
              <div className="space-y-2">
                {data.top_players.map((p, i) => (
                  <div key={i} className={`flex items-center justify-between py-2 border-b border-white/5 last:border-0 ${i === 0 ? "bg-[#CCFF00]/5 -mx-2 px-2 border-l-2 border-l-[#CCFF00]" : ""}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {i === 0 ? <Medal size={16} weight="fill" className="text-[#CCFF00] shrink-0" /> : <span className="font-['Barlow_Condensed'] text-lg font-black text-gray-600 w-6">{String(i + 1).padStart(2, "0")}</span>}
                      {p.user_id ? <Link to={`/p/${p.user_id}`} className={`font-semibold hover:text-[#CCFF00] text-sm truncate ${i === 0 ? "text-[#CCFF00]" : "text-white"}`}>{p.name}</Link> : <span className="font-semibold text-white text-sm truncate">{p.name}</span>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[10px] text-gray-500 font-['JetBrains_Mono']">{p.wins}/{p.matches}</span>
                      <span className="font-['JetBrains_Mono'] text-[#CCFF00] font-bold text-sm">{p.points}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="public-club-matches">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Trophy size={14} />Resultados</p>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4">Partidos Recientes</h2>
            {data.recent_matches.length === 0 ? <p className="text-gray-500 text-sm">Sin partidos aun</p> : (
              <div className="space-y-2">
                {data.recent_matches.map((m) => {
                  const s1 = m.sets.filter((s) => s.winner === 1).length;
                  const s2 = m.sets.filter((s) => s.winner === 2).length;
                  const setScores = m.sets.map((s) => `${s.pair1_games}-${s.pair2_games}`).join(" · ");
                  return (
                    <div key={m.id} className="bg-[#141414] border border-white/5 p-3">
                      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-['JetBrains_Mono']">{m.date}{m.time ? ` · ${m.time}` : ""} · {m.format === "short" ? "Tabla 4-0" : "2 de 3 sets"}</span>
                        {m.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[9px]">Cat. {m.category}</Badge>}
                      </div>
                      <p className={`text-sm truncate ${m.winner_pair === 1 ? "text-[#CCFF00] font-semibold" : "text-white"}`}>{m.pair1.p1_name} / {m.pair1.p2_name}</p>
                      <p className={`text-sm truncate ${m.winner_pair === 2 ? "text-[#CCFF00] font-semibold" : "text-white"}`}>{m.pair2.p1_name} / {m.pair2.p2_name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="font-['JetBrains_Mono'] text-[11px] text-gray-500">{setScores}</span>
                        <span className="font-['Barlow_Condensed'] font-black text-xl">
                          <span className={m.winner_pair === 1 ? "text-[#CCFF00]" : "text-gray-500"}>{s1}</span>
                          <span className="text-gray-600">-</span>
                          <span className={m.winner_pair === 2 ? "text-[#CCFF00]" : "text-gray-500"}>{s2}</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="public-club-tournaments">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FFB800] mb-1 flex items-center gap-2"><Trophy size={14} />Agenda</p>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4">Proximos Torneos</h2>
            {data.upcoming_tournaments.length === 0 ? <p className="text-gray-500 text-sm">Sin torneos proximos</p> : (
              <div className="space-y-2">
                {data.upcoming_tournaments.map((t) => (
                  <div key={t.id} className="bg-[#141414] border border-white/5 p-3">
                    <p className="text-sm text-white font-semibold">{t.name}</p>
                    <div className="flex gap-2 text-[10px] text-gray-500 mt-1 flex-wrap uppercase tracking-widest">
                      <span>{t.start_date}</span>
                      <span>{t.players?.length || 0}/{t.max_players}</span>
                      {t.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[9px] py-0">Cat. {t.category}</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="public-club-retas">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#FF3B30] mb-1 flex items-center gap-2"><Sword size={14} />Agenda</p>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight mb-4">Proximas Retas</h2>
            {data.upcoming_retas.length === 0 ? <p className="text-gray-500 text-sm">Sin retas proximas</p> : (
              <div className="space-y-2">
                {data.upcoming_retas.map((r) => (
                  <div key={r.id} className="bg-[#141414] border border-white/5 p-3">
                    <p className="text-sm text-white font-semibold">{r.challenger_name} vs {r.challenged_name}</p>
                    <div className="flex gap-2 text-[10px] text-gray-500 mt-1 uppercase tracking-widest">
                      <span>{r.date}{r.time ? ` · ${r.time}` : ""}</span>
                      <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[9px] py-0">{r.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="club-share-buttons">
          <Button onClick={shareWA} className="bg-green-500 text-white hover:bg-green-600 uppercase text-xs font-bold" data-testid="club-share-whatsapp"><WhatsappLogo size={14} className="mr-1" />WhatsApp</Button>
          <Button onClick={copy} variant="outline" className="border-white/20 text-gray-300 hover:text-white uppercase text-xs font-bold" data-testid="club-share-copy"><LinkSimple size={14} className="mr-1" />Copiar enlace</Button>
          <Button onClick={shareNative} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-xs font-bold ml-auto" data-testid="club-share-native"><ShareNetwork size={14} className="mr-1" />Compartir</Button>
        </div>

        <p className="text-xs text-gray-600 text-center pt-6">
          <Link to="/register" className="text-[#CCFF00] hover:underline uppercase tracking-wider">Registrate gratis</Link> y unete al club
        </p>
      </div>
    </div>
  );
}
